import { Crown, Lock, Sparkles } from "lucide-react";

import { useSession } from "@/contexts/SessionContext";

/**
 * Shown when a trial / expired user hits `/strategy` directly.
 * Styled as a polished locked panel + clear "Upgrade" CTA that pre-selects
 * the Creator tier in the SubscriptionDrawer.
 */
export default function StrategyLocked() {
    const { openSubscriptionDrawer } = useSession();
    const upgrade = () =>
        openSubscriptionDrawer({
            code: "tier_required",
            required: "creator",
            feature: "Strategy generation",
        });

    return (
        <section
            className="relative mx-auto mt-6 max-w-xl overflow-hidden rounded-3xl border border-dashed border-[#1F2A40] bg-[#111726] p-10 text-center"
            data-testid="strategy-locked"
        >
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-70"
                style={{
                    background:
                        "radial-gradient(circle at 50% 0%, rgba(47,88,205,0.18) 0%, transparent 60%), radial-gradient(circle at 50% 100%, rgba(0,245,212,0.08) 0%, transparent 60%)",
                }}
            />
            <div className="relative flex flex-col items-center gap-5">
                <div
                    className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[#1F2A40] bg-[#0B0F19] text-[#00F5D4]"
                    style={{
                        boxShadow:
                            "0 0 22px rgba(0,245,212,0.2), inset 0 0 0 1px rgba(0,245,212,0.3)",
                    }}
                    data-testid="strategy-locked-icon"
                >
                    <Lock className="h-6 w-6" strokeWidth={2} />
                </div>
                <div className="flex flex-col gap-2">
                    <h2 className="text-2xl font-medium text-[#E8ECF5]">
                        Strategy is a Creator feature
                    </h2>
                    <p className="mx-auto max-w-md text-sm leading-relaxed text-[#8A93A6]">
                        Unlock multi-week content roadmaps, platform-aware
                        hooks, and daily post plans — orchestrated by Pulse to
                        compound your audience growth.
                    </p>
                </div>
                <ul className="flex flex-col items-start gap-2 text-left text-xs text-[#E8ECF5]">
                    <li className="flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5 text-[#00F5D4]" />
                        7, 15 or 30-day roadmap tailored to your niche
                    </li>
                    <li className="flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5 text-[#00F5D4]" />
                        Daily hooks, titles, captions, hashtags & format picks
                    </li>
                    <li className="flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5 text-[#00F5D4]" />
                        One-click send to your Content Library
                    </li>
                </ul>
                <button
                    type="button"
                    onClick={upgrade}
                    data-testid="strategy-locked-upgrade-btn"
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-all hover:shadow-[0_0_26px_rgba(0,245,212,0.45)]"
                >
                    <Crown className="h-4 w-4" />
                    Upgrade to Creator
                </button>
                <p className="font-mono text-[11px] italic text-[#8A93A6]">
                    Creator plan — $24/mo simulated · includes 200 NXT monthly
                </p>
            </div>
        </section>
    );
}
